import { useMutation } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';

import { EntryTypeBlockStatus, EntryTypeFieldType } from 'chaika-enums';
import { FieldParams } from 'chaika-types';

import { BlockApi } from '@api/BlockApi';
import { queryClient } from '@store/queryClient';
import { systemFieldTypesSet } from '@template/store/entryType/EntryTypeField';
import { translate } from '@chaika/i18n/helper';
import { assertIsDefined } from '@utils/assertIsDefined';

import { BlockData } from '../types';
import { normalizeBlock, toBlockSaveForm } from '../utils/blockData';
import { blockKeys } from './blockQueries';

interface SaveBlockParams {
    blockId: string;
    version: number;
    block: BlockData;
}

export function useUpdateBlock() {
    return useMutation({
        mutationFn: ({ blockId, version, block }: SaveBlockParams) =>
            BlockApi.update(blockId, version, toBlockSaveForm(block)),
    });
}

/**
 * Создаёт черновик следующей версии активного блока. Если черновик уже существует,
 * бэкенд отвечает ошибкой — тогда подхватываем существующий черновик из списка.
 */
export function useCreateBlockDraft() {
    return useMutation({
        mutationFn: async ({ blockId, version, block }: SaveBlockParams) => {
            try {
                await BlockApi.create(blockId, toBlockSaveForm(block));

                return { ...block, status: EntryTypeBlockStatus.Draft, version: version + 1 };
            } catch {
                return normalizeBlock(await findBlockDraft(blockId));
            }
        },
        onSuccess: draft => {
            queryClient.setQueryData(blockKeys.version(draft.id, draft.version), draft);
        },
    });
}

export function useActivateBlock() {
    return useMutation({
        mutationFn: ({ blockId, version }: { blockId: string; version: number }) =>
            BlockApi.updateStatus(blockId, version, EntryTypeBlockStatus.Active),
        onSuccess: (_data, { blockId, version }) => {
            queryClient.setQueryData<BlockData>(blockKeys.version(blockId, version), block =>
                block ? { ...block, status: EntryTypeBlockStatus.Active } : block,
            );
        },
    });
}

export function useCopyBlock() {
    return useMutation({
        mutationFn: async ({ block }: { block: BlockData }) => {
            const id = uuid();

            await BlockApi.create(id, toBlockCopyForm(block));

            return normalizeBlock(await BlockApi.getBlockVersion(id, 1));
        },
        onSuccess: copy => {
            queryClient.setQueryData(blockKeys.version(copy.id, copy.version), copy);
        },
    });
}

async function findBlockDraft(blockId: string) {
    const blocks = await BlockApi.getList();
    const draft = blocks.find(block => block.id === blockId && block.status === EntryTypeBlockStatus.Draft);
    assertIsDefined(draft, `Block ${blockId} draft was not created and no existing draft was found`);

    return draft;
}

function toBlockCopyForm(block: BlockData) {
    const { title, name, fields, titleTranslations } = toBlockSaveForm(block);

    return {
        titleTranslations,
        title: `${title} (${translate('copySingle')})`,
        name: `${name} (${translate('copySingle')})`,
        fields: fields.map(field => ({ ...field, name: makeCopiedFieldName(field) })),
    };
}

function makeCopiedFieldName({ name, type }: FieldParams): string {
    const hasFixedName = systemFieldTypesSet.has(type) || type === EntryTypeFieldType.Computed;

    return hasFixedName ? name : `new ${name}`;
}
